import API from "./API";

type AddressBase = {
  contactType: string;
  contactFirstName?: string;
  contactLastName?: string;
  companyName?: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  country: string;
  zip: string;
};

export type InitSessionPayload = {
  // Event
  eventType: string;
  name: string;
  dates: string[];
  setUpDates?: string[];
  tearDownDates?: string[];
  eventFrequency: string;
  averageDailyAttendance: number;
  includeRehearsalDinner?: boolean | null;
  includeBreakfastAfterWedding?: boolean | null;

  // Coverage
  gll: { occurrence: number | null; aggregate: number | null };
  additionalCoverage: Array<{ type: string; value: boolean | number | null }>;

  // Venue
  venueSearchType: string;
  venueAddress: Array<{
    placeId: string | null;
    companyName: string | null;
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    country: string;
    zip: string;
    utcOffset: number;
    municipalTaxCode: string | null;
    facilityCode: string | null;
    venueCode: string | null;
  }>;

  // Renter / contact
  renterAddress: [AddressBase & { companyType?: string }];
  insuranceContactPhone: string;
  insuranceContactEmail: string;
  insuranceContactAddress: [AddressBase];
  billingAddress?: [AddressBase];

  // Policy conditions
  consentConfirmed: boolean;
  excludedActivitiesConfirmed: boolean;
  northDakotaDisclosure?: boolean | null;
  isKentuckyEntity: boolean;
  isFederalEntity: boolean;
  lossClaims?: boolean | null;
  security?: boolean | null;

  // Underwriting
  underwritingQuestions: Array<{ code: string; answer: unknown }>;

  // Referral / codes
  sourceCode?: string | null;
  producerCode?: string | null;
  subProducerCode?: string | null;
  facilityCode?: string | null;
  boilerplate?: unknown[] | null;

  // Additional insureds
  additionalInsureds: Array<{
    type: string;
    address?: {
      companyName?: string | null;
      address1?: string | null;
      address2?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      zip?: string | null;
    };
  }>;

  // Total amount
  amountDetails: {
    totalAmount: number;
    currency: string;
  };

  // Idempotency: stable per browser tab; lets the backend reuse a reserved
  // PolicyNumber/staging row when the user re-opens the PortalOne modal.
  clientSessionId?: string;

  userEntryData: {
    data: any;
    formUniqId: any;
  };

  dashedInsuredName: string;
};

export type InitSessionResponse = {
  sessionId: string;
  accessToken: string;
  policyNumber: string;
  userEntryData: {
    data: any;
    formUniqId: any;
  };
  dashedInsuredName: string;
};

export type ConfirmPaymentPayload = {
  policyNumber: string;
  paymentCompleteEvent: {
    sessionId: string;
    transactionId: string | number;
    totalPaymentAmount: string | number;
    [key: string]: unknown;
  };
};

class PaymentService {
  api: API = null;

  constructor(api: API) {
    this.api = api;
  }

  createCoverage(paymentInfo: unknown) {
    return this.api.post({
      location: "/coverage",
      body: JSON.stringify(paymentInfo),
    });
  }

  getPolicyDocs(url: string) {
    return this.api.getDownload({
      location: url,
      config: {
        responseType: "blob",
      },
    });
  }

  getDocumentsStatus(coverageOrder: string) {
    return this.api.get({
      location: "/policyDocumentsStatus",
      params: { coverageOrder },
    });
  }

  initSession(payload: InitSessionPayload) {
    return this.api.post({
      location: "/payment/init",
      body: JSON.stringify(payload),
    });
  }

  confirmPayment(payload: ConfirmPaymentPayload) {
    return this.api.post({
      location: "/payment/confirm",
      body: JSON.stringify(payload),
    });
  }
}

export default PaymentService;
